/**
 * Deterministic placement for procedural rock/boulder scatter (issue #63):
 * ground-level visual anchoring near the route AND the fixed inspection
 * viewpoints. Reuses corridor.ts's `distanceToPolylineM` — the exact same
 * "near route" filter roadWalls/vegetation/water already share — plus
 * an additional per-viewpoint radius, so a rock scatter can anchor a
 * standalone viewpoint even with no route corridor nearby.
 *
 * Pure and three.js-free, unit-tested in Node. The y elevation is NOT set
 * here — rocks.ts grounds each instance on the DTM collider (hard constraint
 * #6); this module has no bearing on any LOS result (hard constraint #1,
 * guarded by rocks.guard.test.ts).
 */
import type { LocalXZ } from '../core/coordinates';
import { distanceToPolylineM } from './corridor';
import { ringBounds } from './vegetationPlacement';
import type { RockPreset } from './rockGeometry';
import type { TarmacClearance } from './tarmacClearance';

/** Deterministic fractional hash in [0, 1) — the same Math.sin idiom used
 * across this codebase's placement modules. */
function hash01(i: number): number {
  const x = Math.sin(i * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/** Only roads/route within this distance of a rock candidate count as "near
 * route" (rocks are ground-level, so a tighter reach than trees is
 * appropriate — they only read up close). */
export const DEFAULT_ROCK_CORRIDOR_M = 45;
/** Radius (m) around a fixed inspection viewpoint within which rocks are
 * also planted, independent of the route corridor. */
export const DEFAULT_ROCK_VIEWPOINT_RADIUS_M = 120;
/** Base grid spacing (m) between candidate rocks at densityScale 1. */
export const ROCK_GRID_SPACING_M = 9;
/** Extra per-instance size variety multiplied onto a preset's base radius. */
export const ROCK_SCALE_RANGE: [number, number] = [0.5, 1.6];

const ROCK_KEEP_PROB = 0.45;
/** Bounded default so a fresh scatter always lands in the issue's
 * "tens to low hundreds" range even with no explicit cap. */
const DEFAULT_MAX_INSTANCES = 220;
/** Safety clamp on the candidate grid, mirroring sampleAreaInstances: a
 * route+viewpoint bounding box must never generate an unbounded candidate
 * count regardless of its size. */
const MAX_ROCK_CANDIDATES = 20000;

export interface RockInstance {
  x: number;
  z: number;
  preset: RockPreset;
  /** Card yaw (radians) for orientation variety. */
  rotationY: number;
  /** Extra per-instance scale multiplier — see ROCK_SCALE_RANGE. */
  scale: number;
}

export interface RockPlacementOptions {
  corridorMaxDistM?: number;
  /** Fixed inspection viewpoints (local x/z) rocks also anchor near. */
  viewpoints?: LocalXZ[];
  viewpointRadiusM?: number;
  /** Scales the per-slot keep probability, i.e. overall planting density
   * (issue #17 low tier: fewer instances). Default 1. */
  densityScale?: number;
  /** Hard cap on returned instances (issue #63 AC: "tens to low hundreds").
   * Default DEFAULT_MAX_INSTANCES. */
  maxInstances?: number;
  /** Carriageway clearance (issue #79): drops rocks on the route ribbon or a
   * nearby tarmac road centreline. Applied before the maxInstances thinning. */
  clearance?: TarmacClearance;
}

function withinReach(
  p: LocalXZ,
  corridor: LocalXZ[],
  corridorMaxDistM: number,
  viewpoints: LocalXZ[],
  viewpointRadiusM: number,
): boolean {
  if (corridor.length > 0 && distanceToPolylineM(p, corridor) <= corridorMaxDistM) return true;
  for (const vp of viewpoints) {
    if (Math.hypot(p.x - vp.x, p.z - vp.z) <= viewpointRadiusM) return true;
  }
  return false;
}

/** Weighted preset pick — boulders are the commonest, cobbles the rarest
 * accent (mirrors vegetationPlacement.ts's pickKind weighting idiom). */
function pickPreset(slotSeed: number): RockPreset {
  const r = hash01(slotSeed * 4.7);
  if (r < 0.4) return 'boulder';
  if (r < 0.7) return 'angular';
  if (r < 0.88) return 'slab';
  return 'cobble';
}

/**
 * Seed-samples rock instances on a jittered grid clipped to the union of the
 * route corridor and any viewpoint radii. Deterministic in (corridor,
 * viewpoints, options): a given AOI plants the same rocks on every load.
 */
export function sampleRockInstances(corridor: LocalXZ[], options: RockPlacementOptions = {}): RockInstance[] {
  const viewpoints = options.viewpoints ?? [];
  if (corridor.length === 0 && viewpoints.length === 0) return [];

  const corridorMaxDistM = options.corridorMaxDistM ?? DEFAULT_ROCK_CORRIDOR_M;
  const viewpointRadiusM = options.viewpointRadiusM ?? DEFAULT_ROCK_VIEWPOINT_RADIUS_M;
  const density = Math.max(1e-3, options.densityScale ?? 1);
  const maxInstances = options.maxInstances ?? DEFAULT_MAX_INSTANCES;
  // Only expand the candidate bbox by the reach of whichever source is
  // actually present, so a route-only or viewpoint-only call doesn't pay for
  // the other's (possibly larger) radius.
  const reach = Math.max(corridor.length > 0 ? corridorMaxDistM : 0, viewpoints.length > 0 ? viewpointRadiusM : 0);

  const boundsPoints = corridor.length > 0 ? corridor.concat(viewpoints) : viewpoints;
  const b = ringBounds(boundsPoints);
  const minX = b.minX - reach;
  const maxX = b.maxX + reach;
  const minZ = b.minZ - reach;
  const maxZ = b.maxZ + reach;

  let step = ROCK_GRID_SPACING_M / Math.sqrt(density);
  const bboxArea = Math.max(1, (maxX - minX) * (maxZ - minZ));
  const minStepForCap = Math.sqrt(bboxArea / MAX_ROCK_CANDIDATES);
  if (minStepForCap > step) step = minStepForCap;
  const nx = Math.max(1, Math.ceil((maxX - minX) / step));
  const nz = Math.max(1, Math.ceil((maxZ - minZ) / step));
  const [sMin, sMax] = ROCK_SCALE_RANGE;

  const candidates: RockInstance[] = [];
  for (let gi = 0; gi < nx; gi++) {
    const cellX = minX + (gi + 0.5) * step;
    for (let gj = 0; gj < nz; gj++) {
      const cellZ = minZ + (gj + 0.5) * step;
      const s = gi * 92821 + gj * 68917 + 1;
      if (hash01(s * 3.7) >= ROCK_KEEP_PROB * density) continue;
      const px = cellX + (hash01(s * 1.1) - 0.5) * step * 0.9;
      const pz = cellZ + (hash01(s * 2.3) - 0.5) * step * 0.9;
      const p: LocalXZ = { x: px, z: pz };
      if (!withinReach(p, corridor, corridorMaxDistM, viewpoints, viewpointRadiusM)) continue;
      // Carriageway keep-off (issue #79): drop rocks on the route ribbon or a
      // nearby tarmac road centreline, before the deterministic cap thinning.
      if (options.clearance && !options.clearance.isClear(p)) continue;
      candidates.push({
        x: px,
        z: pz,
        preset: pickPreset(s),
        rotationY: hash01(s * 8.9) * Math.PI * 2,
        scale: sMin + hash01(s * 5.1) * (sMax - sMin),
      });
    }
  }

  if (candidates.length <= maxInstances) return candidates;
  // Deterministically thin to the hard cap via a stable per-candidate hash
  // rank (not array/grid traversal order), so the kept subset is a uniform
  // sample rather than being biased toward one corner of the grid.
  return candidates
    .map((c, i) => ({ c, rank: hash01(c.x * 7.349 + c.z * 4.111 + i * 0.017) }))
    .sort((a, b2) => a.rank - b2.rank)
    .slice(0, maxInstances)
    .map((e) => e.c);
}
